import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ProtectionView } from "@ti/core";
import { ShieldCheck } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { ClaimForm } from "@/features/protect/claim-form";
import { PROTECT_STATE } from "@/features/protect/labels";
import { FooterSeal, joinNames, PublicError, PublicFrame, PublicSection, possessive, useLightTheme, Wordmark } from "@/features/protect/public-layout";
import { AssistantList, EmbedBadge, Faq, FinePrint, ScenarioList } from "@/features/protect/sections";
import { q } from "@/lib/api";
import { fmtInt, fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/protect/$orgSlug")({
  component: ProtectionPage,
});

function ProtectionPage() {
  const { orgSlug } = Route.useParams();
  const query = useQuery(q.protection(orgSlug));
  useLightTheme();

  const view = query.data;
  useEffect(() => {
    document.title = view ? `${view.org.name} · AI assistant protection` : "AI assistant protection · traces.insure";
  }, [view]);

  return (
    <PublicFrame
      header={<TopBar org={view?.org} failed={!!query.error} />}
      footer={
        <>
          <span>
            {view ? `Insurance provided by ${joinNames(view.carriers)}, purchased by ${view.org.name}. ` : null}traces.insure administers coverage and evidence; it does not decide claims.
          </span>
          <FooterSeal />
        </>
      }
    >
      {query.error ? (
        <PublicError
          error={query.error}
          retry={() => void query.refetch()}
          notFoundTitle="We couldn't find this protection page"
          notFoundBody="The link may be mistyped, or the page may have moved. Please use the link from the company whose assistant you were chatting with, or contact their support team."
          errorTitle="We couldn't load this protection page"
        />
      ) : view ? (
        <Protection view={view} />
      ) : (
        <ProtectionSkeleton />
      )}
    </PublicFrame>
  );
}

function TopBar({ org, failed }: { org?: ProtectionView["org"]; failed: boolean }) {
  return (
    <>
      <div className="flex min-w-0 items-center gap-2.5">
        {org ? (
          <>
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-fg text-[12px] font-bold tracking-tight text-background" aria-hidden>
              {org.logoText}
            </span>
            <span className="truncate text-[15px] font-semibold tracking-tight">{org.name}</span>
          </>
        ) : failed ? (
          <Wordmark className="text-[15px] font-semibold" />
        ) : (
          <Skeleton className="h-8 w-40" />
        )}
      </div>
      {org ? (
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-surface-2 px-2.5 py-1 text-xs text-fg-2">
          <ShieldCheck className="size-3.5 text-accent" aria-hidden />
          <span className="hidden sm:inline">Protected by</span>
          <Wordmark />
        </span>
      ) : null}
    </>
  );
}

function Protection({ view }: { view: ProtectionView }) {
  const { org } = view;
  const covered = view.scenarios.filter((s) => PROTECT_STATE[s.state].protected);
  const maxLimit = covered.reduce((m, s) => (s.perClaimLimit.amount > m.amount ? s.perClaimLimit : m), covered[0]?.perClaimLimit ?? { amount: 0, currency: "USD" });

  return (
    <div className="flex flex-col gap-12">
      <section aria-labelledby="hero-title" className="flex flex-col gap-5">
        <p className="inline-flex items-center gap-1.5 self-start rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent">
          <ShieldCheck className="size-3.5" aria-hidden /> Free protection for people who use {possessive(org.name)} AI assistants
        </p>
        <h1 id="hero-title" className="max-w-3xl text-[28px] font-semibold leading-tight tracking-tight text-balance sm:text-4xl">
          When {possessive(org.name)} AI assistants make a mistake, you're protected
        </h1>
        <p className="max-w-2xl text-[15px] leading-relaxed text-fg-2">
          If one of the assistants gets something wrong in an area listed below and you lose money because of it, you can claim up to {fmtMoney(maxLimit)} — paid by{" "}
          {possessive(org.name)} insurer, at no cost to you.
        </p>
        <div className="flex flex-wrap gap-3">
          <a href="#file-claim" className={cn(buttonVariants({ size: "lg" }), "h-10 px-5 text-sm")}>
            File a claim
          </a>
          <a href="#covered" className={cn(buttonVariants({ variant: "outline", size: "lg" }), "h-10 px-5 text-sm")}>
            What's covered
          </a>
        </div>
        <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <HeroFact label="Per claim, up to" value={fmtMoney(maxLimit)} />
          <HeroFact label="Areas covered" value={fmtInt(covered.length)} />
          <HeroFact label="Claims paid (12 mo)" value={fmtInt(view.claimsPaid12m)} />
          <HeroFact label="Cost to you" value="Nothing" />
        </dl>
      </section>

      <PublicSection id="covered" title="What's covered" description="Live status from continuous monitoring of the assistants. Areas with a fix in progress stay covered.">
        <ScenarioList scenarios={view.scenarios} />
      </PublicSection>

      <PublicSection id="assistants" title="Which assistants are covered" description={`Your conversations with these ${org.name} assistants are covered.`}>
        <AssistantList assistants={view.assistants} />
      </PublicSection>

      <PublicSection id="file-claim" title="File a claim" description="Tell us what happened. We'll find the conversation and collect the evidence for you — it takes about two minutes.">
        <div className="rounded-2xl border bg-surface p-5 sm:p-7">
          <ClaimForm orgSlug={org.slug} orgName={org.name} maxLimit={maxLimit} scenarios={covered} />
        </div>
      </PublicSection>

      <PublicSection id="faq" title="Questions">
        <Faq view={view} maxLimit={maxLimit.amount} />
      </PublicSection>

      <PublicSection id="policy" title="Policy details">
        <FinePrint view={view} />
      </PublicSection>

      <PublicSection
        id="badge"
        title={<span className="text-base">For the {org.name} team</span>}
        description="Add the Protected badge wherever people talk to your assistants, so they know where to turn if something goes wrong."
        className="border-t pt-10"
      >
        <EmbedBadge orgSlug={org.slug} orgName={org.name} />
      </PublicSection>
    </div>
  );
}

function HeroFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border bg-surface px-3 py-2.5 sm:px-4 sm:py-3">
      <dt className="text-xs text-fg-3">{label}</dt>
      <dd className="mt-0.5 text-[15px] font-semibold tabular sm:text-lg">{value}</dd>
    </div>
  );
}

function ProtectionSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading protection details">
      <Skeleton className="h-6 w-56 rounded-full" />
      <Skeleton className="h-20 w-full max-w-2xl" />
      <Skeleton className="h-5 w-full max-w-xl" />
      <Skeleton className="mt-4 h-64 w-full rounded-2xl" />
      <Skeleton className="h-48 w-full rounded-2xl" />
    </div>
  );
}
